import React, { useState, useEffect } from "react";
import { FiChevronRight } from "react-icons/fi";
import { TiArrowSortedUp, TiArrowSortedDown } from "react-icons/ti";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useGetAllCoins24hChangesQuery } from "../../features/trade&coins/coinsChangesSlice";
import { Zap, Menu, X } from "lucide-react";
import { VipTradingBanner } from "../../components/Dashboard/vip-trading/VipTradingBanner";
import { COIN_ICONS } from "../../components/Dashboard/coins/CoinIcons";

// Helper function to format percentage and determine color with arrow icons
const formatChange = (change: number | null) => {
  if (change === null || change === undefined) return { text: "", color: "text-gray-400", icon: null };

  const isPositive = change >= 0;
  const color = isPositive ? "text-green-400" : "text-red-400";
  const sign = isPositive ? "+" : "";
  const icon = isPositive ? TiArrowSortedUp : TiArrowSortedDown;

  return {
    text: `${sign}${change.toFixed(2)}%`,
    color: color,
    icon: icon
  };
};

interface CoinLinkProps {
  to: string;
  coinName: string;
  apiCoinName: string;
  icon: React.ReactNode;
  isActive: boolean;
  change: ReturnType<typeof formatChange>;
}

const CoinLink = ({ to, coinName, icon, isActive, change, onNavigate }: CoinLinkProps & { onNavigate?: () => void }) => {
  return (
    <NavLink end to={to} className="block" onClick={onNavigate}>
      {({ isActive: navIsActive }) => {
        const active = navIsActive || isActive;
        return (
          <button
            className={`w-full flex items-center justify-between p-4 rounded-xl transition-all duration-200 ${active
              ? "bg-gray-800 border border-primary"
              : "bg-gray-800/50 hover:bg-gray-800 border border-gray-700/50"
              }`}
          >
            <div className="flex items-center gap-3 text-left">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center transition-all ${active ? "bg-primary/20 border-2 border-primary" : "bg-gray-700 border-2 border-gray-600"
                }`}>
                <div className="text-primary text-sm font-semibold">
                  {icon}
                </div>
              </div>
              <div>
                <p className={`font-semibold text-sm ${active ? "text-primary" : "text-white"}`}>
                  {coinName.replace("Sell ", "")}
                </p>
                {change.text && (
                  <p className={`text-xs font-semibold mt-0.5 ${change.color}`}>
                    {change.text}
                  </p>
                )}
              </div>
            </div>
            {change.text && !active && change.icon && (
              <div className="text-right">
                {React.createElement(change.icon, { className: `w-4 h-4 ${change.color}` })}
              </div>
            )}
          </button>
        );
      }}
    </NavLink>
  );
};

export const QuickSell = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // Fetch 24h changes for all coins
  const { data: coinsChanges, isLoading: isChangesLoading } = useGetAllCoins24hChangesQuery();

  // Close sidebar when route changes (coin is selected)
  useEffect(() => {
    setIsSidebarOpen(false);
  }, [location.pathname]);

  // Helper function to get 24h change for a coin
  const getCoin24hChange = (coinName: string) => {
    if (!coinsChanges || !coinsChanges[coinName]) return null;
    return coinsChanges[coinName].usd_24h_change;
  };

  const coins = [
    { to: "/app/quick-sell/bitcoin", coinName: "Sell Bitcoin", apiCoinName: "bitcoin", icon: COIN_ICONS.bitcoin },
    { to: "/app/quick-sell/dogecoin", coinName: "Sell Dogecoin", apiCoinName: "dogecoin", icon: COIN_ICONS.dogecoin },
    { to: "/app/quick-sell/ethereum", coinName: "Sell Ethereum", apiCoinName: "ethereum", icon: COIN_ICONS.ethereum },
    { to: "/app/quick-sell/xrp", coinName: "Sell XRP", apiCoinName: "xrp", icon: COIN_ICONS.xrp },
    { to: "/app/quick-sell/usdt", coinName: "Sell USDT", apiCoinName: "usdt", icon: COIN_ICONS.usdt },
    { to: "/app/quick-sell/usdc", coinName: "Sell USDC", apiCoinName: "usdc", icon: COIN_ICONS.usdc },
    { to: "/app/quick-sell/litecoin", coinName: "Sell Litecoin", apiCoinName: "litecoin", icon: COIN_ICONS.litecoin },
    { to: "/app/quick-sell/tron", coinName: "Sell TRON (TRX)", apiCoinName: "tron", icon: COIN_ICONS.tron },
    { to: "/app/quick-sell/solana", coinName: "Sell SOL (Solana)", apiCoinName: "solana", icon: COIN_ICONS.solana },
    { to: "/app/quick-sell/binance-smart-chain", coinName: "Sell BNB (Binance Smart Chain)", apiCoinName: "binance-smart-chain", icon: COIN_ICONS["binance-smart-chain"] },
  ];

  return (
    <div className="font-dashboard pt-4 pb-12 md:px-4 min-h-screen">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Mobile Header with Toggle Button */}
        <div className="lg:hidden mb-6 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 rounded-lg bg-primary flex items-center justify-center">
              <span className="text-primary-foreground text-xs font-bold">⚡</span>
            </div>
            <h2 className="text-lg font-bold text-white">Quick Sell</h2>
          </div>
          <button
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            className="p-2 rounded-lg bg-gray-800 text-white hover:bg-gray-700 transition"
            aria-label="Toggle sidebar"
          >
            {isSidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Mobile Overlay */}
          {isSidebarOpen && (
            <div
              className="fixed inset-0 bg-black/60 z-40 lg:hidden transition-opacity duration-300"
              onClick={() => setIsSidebarOpen(false)}
            />
          )}

          {/* Left Sidebar - Quick Sell */}
          <div
            className={`lg:col-span-1 transition-transform duration-300 ease-in-out ${
              isSidebarOpen
                ? "fixed inset-y-0 left-0 z-50 w-80 translate-x-0 lg:relative lg:inset-auto lg:z-auto lg:w-auto lg:translate-x-0"
                : "fixed inset-y-0 left-0 z-50 w-80 -translate-x-full lg:relative lg:left-0 lg:z-auto lg:w-auto lg:translate-x-0"
            }`}
          >
            <div className="bg-black rounded-2xl p-6 sticky top-24 h-full lg:h-auto overflow-y-auto lg:overflow-y-visible">
              <div className="flex items-center justify-between mb-6 lg:justify-start">
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-lg bg-primary flex items-center justify-center">
                    <span className="text-primary-foreground text-xs font-bold">⚡</span>
                  </div>
                  <h2 className="text-lg font-bold text-white">Quick Sell</h2>
                </div>
                <button
                  onClick={() => setIsSidebarOpen(false)}
                  className="lg:hidden p-2 rounded-lg text-white hover:bg-gray-800 transition"
                  aria-label="Close sidebar"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="space-y-4">
                {coins.map((coin) => {
                  const change = formatChange(getCoin24hChange(coin.apiCoinName));
                  return (
                    <CoinLink
                      key={coin.to}
                      to={coin.to}
                      coinName={coin.coinName}
                      apiCoinName={coin.apiCoinName}
                      icon={coin.icon}
                      isActive={false}
                      change={change}
                      onNavigate={() => setIsSidebarOpen(false)}
                    />
                  );
                })}
              </div>
              <NavLink 
                end 
                to="/app/quick-sell/history"
                onClick={() => setIsSidebarOpen(false)}
                className="block"
              >
                {({ isActive }) => (
                  <button className="w-full mt-6 p-3 rounded-xl text-gray-400 text-sm font-medium hover:text-white transition">
                    View History
                  </button>
                )}
              </NavLink>
            </div>
          </div>
          {/* Right Content */}
          <div className="lg:col-span-2">
            {/* VIP Trading Banner */}
            <VipTradingBanner className="mb-6" />

            {location.pathname === "/app/quick-sell" ? (
              <div className="bg-black rounded-2xl p-12 flex flex-col items-center justify-center min-h-[60vh]">
                <div className="w-20 h-20 rounded-full bg-primary/10 border-2 border-primary/30 flex items-center justify-center mb-8">
                  <Zap className="w-10 h-10 text-primary" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-8">Select a Coin to Sell</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 w-full max-w-4xl">
                  {coins.map((coin) => {
                    const change = formatChange(getCoin24hChange(coin.apiCoinName));
                    return (
                      <NavLink key={coin.to} to={coin.to}>
                        <button className="w-full p-4 rounded-xl bg-gray-800 border border-gray-700 hover:border-primary/50 hover:bg-gray-800/80 transition-all flex flex-col items-center gap-2">
                          <div className="w-10 h-10 flex items-center justify-center text-primary">
                            {coin.icon}
                          </div>
                          <span className="text-white font-semibold text-sm">
                            {coin.coinName.replace("Sell ", "")}
                          </span>
                          {change.text && (
                            <span className={`text-xs font-semibold ${change.color}`}>
                              {change.text}
                            </span>
                          )}
                        </button>
                      </NavLink>
                    );
                  })}
                </div>
              </div>
            ) : (
              <Outlet />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
